import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { type OrganizationDto, type UserDto } from 'xosign-types';
import useApi from '@/hooks/useApi';

export type UserDtoWithNullableId = Omit<UserDto, 'id'> & { id: string | null };

type UserContextType = {
  hasAccessTo: (path: string) => boolean;
  hasAccessToCurrentRoute: boolean;
  hasKYC: boolean;
  isAdmin: boolean;
  organizationData?: OrganizationDto | null;
  refetchMe: () => Promise<void>;
  refetchOrganization: () => Promise<void>;
  userData: UserDtoWithNullableId;
};

const UserContext = createContext<UserContextType | null>(null);

const adminOnlyRoutePatterns: (string | RegExp)[] = [
  '/invoicing',
  '/account/members',
  '/account/invoicing',
  '/account/subscription',
  '/subscriptionMissing',
];

// Default object because checking existence every time is annoying!
const defaultUserObject: UserDtoWithNullableId = {
  id: null,
  role: 'USER',
  name: '',
  phone: { countryCode: '', number: '', formattedNumber: '' },
  email: '',
  preferences: {},
  hasExternalLogin: false,
  kyc: false,
  emailVerifiedAt: null,
};

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [getMe] = useApi('users.me');
  const [getOrganization] = useApi('organization.me');

  const [user, setUser] = useState<UserDtoWithNullableId>(defaultUserObject);
  const [organization, setOrganization] = useState<OrganizationDto>();

  const hasKYC = user.kyc;
  const isAdmin = user.role === 'ADMIN';

  const hasAccessTo = (path: string) => {
    if (!user.id) {
      return false;
    }

    const requiresAdmin = adminOnlyRoutePatterns.some((pattern) => {
      if (typeof pattern === 'string') {
        return path.startsWith(pattern);
      }

      return pattern.test(path);
    });

    return requiresAdmin ? isAdmin : true;
  };

  const hasAccessToCurrentRoute = hasAccessTo(location.pathname);

  const refetchMe = async () => {
    const response = await getMe();

    if (response?.response) {
      setUser(response.response.data);
    }
  };

  const refetchOrganization = async () => {
    const response = await getOrganization();

    if (response?.response) {
      setOrganization(response.response);
    }
  };

  // Initial data fetch on mount.
  useEffect(() => {
    const fetchInitialData = async () => {
      const [meResponse, orgResponse] = await Promise.all([
        getMe(),
        getOrganization(),
      ]);

      if (meResponse?.response) {
        setUser(meResponse.response.data);
      }

      if (orgResponse?.response) {
        setOrganization(orgResponse.response);
      }
    };

    void fetchInitialData();
  }, [getMe, getOrganization]);

  const contextValue: UserContextType = {
    hasAccessTo,
    hasAccessToCurrentRoute,
    hasKYC,
    isAdmin,
    organizationData: organization,
    refetchMe,
    refetchOrganization,
    userData: user,
  };

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};

const useAuth = (): UserContextType => {
  const ctx = useContext(UserContext);

  if (!ctx) {
    throw new Error('useAuth must be used within UserProvider');
  }

  return ctx;
};

export default useAuth;
